import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { ErrorBox } from "../components/ui";
import { TokelLogo } from "../components/brand";

export default function AuthPage() {
  const [mode, setMode] = useState<"login" | "register" | "forgot">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");
  const [sent, setSent] = useState(false);
  const queryClient = useQueryClient();

  const submit = useMutation({
    mutationFn: async () => {
      if (mode === "login") {
        return api.post("/auth/login", { email, password });
      }
      if (mode === "register") {
        return api.post("/auth/register", {
          email,
          password,
          full_name: fullName,
          workspace_name: workspaceName || email.split("@")[0],
        });
      }
      return api.post("/auth/password-reset/request", { email });
    },
    onSuccess: () => {
      if (mode === "forgot") {
        setSent(true);
        return;
      }
      queryClient.clear();
      window.location.href = "/health";
    },
  });

  return (
    <div className="auth-page">
      <div className="card auth-card">
        <div className="auth-brand">
          <TokelLogo size={30} sub="Аналитика каналов Telegram и MAX" />
        </div>
        <h1>{mode === "login" ? "Вход" : mode === "register" ? "Регистрация" : "Смена пароля"}</h1>
        <p className="lede">
          {mode === "forgot"
            ? "Пришлём ссылку для установки нового пароля."
            : "Здоровье канала — бесплатно. Разбор, поиск возможностей и эксперименты — на платном тарифе."}
        </p>

        {sent ? (
          <p className="muted">
            Если такой адрес зарегистрирован, письмо отправлено. Почтового сервера у
            установки может не быть — тогда письмо лежит в исходящих, и ссылку выдаст
            администратор.
          </p>
        ) : (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submit.mutate();
            }}
          >
            <div className="field">
              <label htmlFor="email">Почта</label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>

            {mode !== "forgot" && (
              <div className="field">
                <label htmlFor="password">Пароль</label>
                <input
                  id="password"
                  type="password"
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  required
                  minLength={10}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
                {mode === "register" && (
                  <span className="faint">Не короче 10 символов, с буквами и цифрами.</span>
                )}
              </div>
            )}

            {mode === "register" && (
              <>
                <div className="field">
                  <label htmlFor="name">Как вас зовут</label>
                  <input
                    id="name"
                    value={fullName}
                    onChange={(event) => setFullName(event.target.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="workspace">Название рабочего пространства</label>
                  <input
                    id="workspace"
                    placeholder="Например, «Городские каналы»"
                    value={workspaceName}
                    onChange={(event) => setWorkspaceName(event.target.value)}
                  />
                </div>
              </>
            )}

            {submit.error && (
              <div style={{ marginBottom: 12 }}>
                <ErrorBox error={submit.error} />
              </div>
            )}

            <button className="primary" type="submit" disabled={submit.isPending} style={{ width: "100%" }}>
              {submit.isPending
                ? "Секунду…"
                : mode === "login"
                  ? "Войти"
                  : mode === "register"
                    ? "Создать пространство"
                    : "Прислать ссылку"}
            </button>
          </form>
        )}

        <div className="auth-switch">
          {mode === "login" && (
            <>
              <button className="ghost small" onClick={() => setMode("register")}>
                Создать пространство
              </button>
              <button className="ghost small" onClick={() => setMode("forgot")}>
                Забыли пароль?
              </button>
            </>
          )}
          {mode !== "login" && (
            <button
              className="ghost small"
              onClick={() => {
                setMode("login");
                setSent(false);
              }}
            >
              Вернуться ко входу
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
